/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IconButton, Button } from "@material-tailwind/react";
import ChatComposer from "../runnerScreens/chatComposer";
import { Phone, Video, Sun, Moon, RefreshCw, AlertTriangle } from "lucide-react";
import Message from "../common/Message";
import OrderStatusFlow from "./OrderStatusFlow";
import AttachmentOptionsFlow from "./AttachmentOptionsFlow";
import CameraPreviewModal from './CameraPreviewModal';
import SpecialInstructionsBanner from "./SpecialInstructionsBanner";
import SpecialInstructionsModal from "./SpecialInstructionsModal";
import ItemSubmissionForm from './ItemSubmissionForm';
import PickupItemForm from "./PickupItemForm";
import VideoCallScreen from "../common/VideoCallScreen";
import CallScreen from "../common/CallScreen";
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useTypingAndRecordingIndicator } from '../../hooks/useTypingIndicator';
import useOrderStore from '../../store/orderStore';
import BarLoader from "../common/BarLoader";
import RecepientContactInformation from "./RecepientContactInformation";
import {
  flushSocketQueue, enqueueSocketEvent,
  submitItemsWithRetry, submitPickupItemWithRetry
} from '../../utils/socketQueue';

import { useCheckOrderExist } from '../../hooks/useCheckOrderExist';
import OrderPendingModal from '../common/OrderPendingModal';


const getCurrentTime = () => {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// ─── Normalise any service-type string → canonical form ───────────────────────
const normaliseServiceType = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/_/g, '-');
  if (s === 'run-errand') return 'run-errand';
  if (s === 'pick-up' || s === 'pick-up' || s === 'pickup') return 'pick-up';
  return null;
};

const STATUS_LABELS = new Set([
  'Arrived at market', 'Purchase in progress', 'Purchase completed',
  'En route to delivery', 'Arrived at delivery location', 'Item delivered',
  'Task completed', 'Arrived at pickup location', 'Item collected',
]);

function RunnerChatScreen({
  initialMessages,
  onMessagesChange,
  onRegisterSetMessages,
  active,
  selectedUser,
  isChatActive,
  text,
  setText,
  dark,
  setDark,
  send,
  setDrawerOpen,
  setInfoOpen,
  runnerId,
  socket,
  chatId: chatIdProp,
  onSpecialInstructions,
  onOrderCreated,
  onPaymentSuccess,
  onDeliveryConfirmed,
  onMessageDeleted,
  showOrderFlow,
  setShowOrderFlow,
  handleOrderStatusClick,
  completedOrderStatuses,
  setCompletedOrderStatuses,
  isAttachFlowOpen,
  setIsAttachFlowOpen,
  handleLocationClick,
  handleAttachClick,
  uploadFileWithProgress,
  replyingTo,
  setReplyingTo,
  cameraOpen,
  capturedImage,
  isPreviewOpen,
  openCamera,
  closeCamera,
  capturePhoto,
  retakePhoto,
  openPreview,
  closePreview,
  setIsPreviewOpen,
  // calls
  videoRef,
  callState,
  callType,
  isMuted,
  isCameraOff,
  formattedDuration,
  remoteUsers,
  localVideoTrack,
  initiateCall,
  acceptCall,
  isSpeakerOn,
  networkQuality,
  toggleSpeaker,
  switchCallCamera,
  declineCall,
  endCall,
  toggleMute,
  toggleCamera,
  isConnecting,
  callError,

  runnerFleetType,
  onStartNewOrder,
  onBackToHome,
  switchCamera,
  facingMode,
  initialDeliveryMarked,
  initialUserConfirmedDelivery,
  initialSpecialInstructions,
  sessionKey,
}) {

  const chatId = chatIdProp
    ?? (selectedUser?._id ? `user-${selectedUser._id}-runner-${runnerId}` : null);
  const [awaitingNewOrder, setAwaitingNewOrder] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const {
    getChat, // eslint-disable-line no-unused-vars
    setCurrentOrder: storeSetCurrentOrder,
    setDeliveryMarked: storeSetDeliveryMarked,
    setUserConfirmedDelivery: storeSetUserConfirmedDelivery,
    setSpecialInstructions: storeSetSpecialInstructions,
    setTaskCompleted: storeSetTaskCompleted,
    setOrderCancelled: storeSetOrderCancelled, // eslint-disable-line no-unused-vars
    setCompletedStatuses: storeSetCompletedStatuses, // eslint-disable-line no-unused-vars
  } = useOrderStore();


  const currentOrder = useOrderStore(s => s.getChat(chatId).currentOrder);
  const deliveryMarked = useOrderStore(s => s.getChat(chatId).deliveryMarked);
  const userConfirmedDelivery = useOrderStore(s => s.getChat(chatId).userConfirmedDelivery);
  const specialInstructions = useOrderStore(s => s.getChat(chatId).specialInstructions);
  const taskCompleted = useOrderStore(s => s.getChat(chatId).taskCompleted);
  const orderCancelled = useOrderStore(s => s.getChat(chatId).orderCancelled);
  const cancellationReason = useOrderStore(s => s.getChat(chatId).cancellationReason);
  const orderMissingFromStore = useOrderStore(
    s => (chatId ? (s._chats[chatId]?.orderMissing ?? false) : false)
  );


  // Thin wrappers so existing code below doesn't need to change call sites
  const setDeliveryMarked = useCallback((v) => storeSetDeliveryMarked(chatId, v), [chatId, storeSetDeliveryMarked]);
  const setUserConfirmedDelivery = useCallback((v) => storeSetUserConfirmedDelivery(chatId, v), [chatId, storeSetUserConfirmedDelivery]);
  const setSpecialInstructions = useCallback((v) => storeSetSpecialInstructions(chatId, v), [chatId, storeSetSpecialInstructions]);
  const setCurrentOrder = useCallback((v) => storeSetCurrentOrder(chatId, v), [chatId, storeSetCurrentOrder]);
  const setTaskCompleted = useCallback((v) => storeSetTaskCompleted(chatId, v), [chatId, storeSetTaskCompleted]);

  const lastSeqRef = useRef(new Map());
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const cameraUsedByItemFormRef = useRef(false);
  const completedStatusesRef = useRef([]);
  const isSyncingFromParent = useRef(false);
  const mountedRef = useRef(true);
  const lastFetchedPayoutOrderIdRef = useRef(null);
  const partnerOnlineRef = useRef(true);
  const deliveryDisputeTimerRef = useRef(null);
  const wasOrderMissingOnMountRef = useRef(orderMissingFromStore);

  const [partnerOnline, setPartnerOnline] = useState(true);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showSpecialInstructionsModal, setShowSpecialInstructionsModal] = useState(false);
  const [showItemSubmissionForm, setShowItemSubmissionForm] = useState(false);
  const [showPickupItemForm, setShowPickupItemForm] = useState(false);
  const [runnerLocation, setRunnerLocation] = useState(null); // eslint-disable-line no-unused-vars
  const [markingDelivery, setMarkingDelivery] = useState(false);

  const currentRequest = selectedUser?.currentRequest ?? currentOrder ?? null;

  const { orderMissing: orderMissingLive } = useCheckOrderExist({
    chatId,
    hasOrder: !!currentOrder?.orderId,
    socket,
    enabled: isChatActive && !!chatId && !orderMissingFromStore,
  });

  const counterpart = selectedUser ? {
    phone: selectedUser.phone,
    firstName: selectedUser.firstName,
    lastName: selectedUser.lastName,
    label: 'Task creator',
  } : null;

  const orderMissing = orderMissingFromStore || orderMissingLive;

  const [backHomeDisabled] = useState(() => {
    try { return localStorage.getItem(`backHome_disabled_${chatId}`) === 'true'; } catch { return false; }
  });

  const [messages, setMessages] = useState(() => {
    if (initialMessages?.length) return initialMessages;
    if (!chatId) return [];
    // Try store first, then fall back to empty
    const storeMessages = useOrderStore.getState().getChat(chatId).messages ?? [];
    return storeMessages;
  });

  const [itemsApproved, setItemsApproved] = useState(() =>
    messages.some(m =>
      (m.type === 'item_submission' || m.messageType === 'item_submission' ||
        m.type === 'pickup_item_submission' || m.messageType === 'pickup_item_submission') && // ← add pickup
      m.status === 'approved'
    )
  );

  const onMessagesChangeRef = useRef(onMessagesChange);
  const [attachFlowResetKey, setAttachFlowResetKey] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (orderMissingLive && chatId) {
      useOrderStore.getState()._patch(chatId, { orderMissing: true });
    }
  }, [orderMissingLive, chatId]);

  useEffect(() => {
    if (initialMessages?.length) {
      processedMessageIds.current = new Set(initialMessages.map(m => m.id).filter(Boolean));
    }
  }, [sessionKey]);

  useEffect(() => {
    if (!socket || !chatId || !runnerId) return;

    const onPartnerOnline = ({ chatId: inc }) => {
      if (inc !== chatId) return;
      partnerOnlineRef.current = true;
      setPartnerOnline(true);
    };

    const onPartnerOffline = ({ chatId: inc }) => {
      if (inc !== chatId) return;
      partnerOnlineRef.current = false;
      setPartnerOnline(false);
    };

    const onPresenceStatus = ({ chatId: inc, isOnline }) => {
      if (inc !== chatId) return;
      partnerOnlineRef.current = isOnline;
      setPartnerOnline(isOnline);
    };

    socket.on('partnerOnline', onPartnerOnline);
    socket.on('partnerOffline', onPartnerOffline);
    socket.on('partnerPresenceStatus', onPresenceStatus);

    socket.emit('userOnline', { userId: runnerId, userType: 'runner', chatId });
    socket.emit('queryPresence', { chatId, userId: runnerId, userType: 'runner' });

    return () => {
      socket.off('partnerOnline', onPartnerOnline);
      socket.off('partnerOffline', onPartnerOffline);
      socket.off('partnerPresenceStatus', onPresenceStatus);
    };
  }, [socket, chatId, runnerId]);

  // Heartbeat — 3s interval to match server's tighter timeout
  useEffect(() => {
    if (!socket || !chatId || !runnerId) return;
    const send = () => { if (socket.connected) socket.emit('presenceHeartbeat'); };
    send();
    const id = setInterval(send, 3000);
    return () => clearInterval(id);
  }, [socket, chatId, runnerId]);

  useEffect(() => {
    if (!socket || !chatId || !runnerId) return;

    const sendHeartbeat = () => {
      if (socket.connected) {
        socket.emit('presenceHeartbeat');
      }
    };

    sendHeartbeat(); // immediate on mount
    const heartbeat = setInterval(sendHeartbeat, 5000);

    return () => clearInterval(heartbeat);
  }, [socket, chatId, runnerId]);

  useEffect(() => {
    if (!chatId) return;
    const existing = useOrderStore.getState().getChat(chatId);

    // Only seed from initialSpecialInstructions if store has nothing yet
    if (!existing.specialInstructions && initialSpecialInstructions) {
      storeSetSpecialInstructions(chatId, initialSpecialInstructions);
    }
  }, [chatId]);


  useEffect(() => { onMessagesChangeRef.current = onMessagesChange; }, [onMessagesChange]);

  const setMessagesAndSync = useCallback((updater) => {
    setMessages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      // ── Always persist to store, even for parent-pushed messages ──
      if (chatId && mountedRef.current) {
        useOrderStore.getState().setMessages(chatId, next);
      }

      if (!isSyncingFromParent.current && onMessagesChangeRef.current && mountedRef.current) {
        queueMicrotask(() => {
          if (mountedRef.current) onMessagesChangeRef.current(next);
        });
      }
      return next;
    });
  }, [chatId])

  useEffect(() => {
    if (!socket || !chatId) return;

    const onUserCancelledOrder = ({ orderId, systemMessage }) => {
      if (currentOrder?.orderId && orderId && currentOrder.orderId !== orderId) return;

      useOrderStore.getState()._patch(chatId, {
        orderCancelled: true,
        cancellationReason: 'user',
      });
      setTaskCompleted(false);

      if (systemMessage) {
        setMessagesAndSync(prev => {
          if (prev.some(m => m.id === systemMessage.id)) return prev;
          return [...prev, systemMessage];
        });
      }
    };

    socket.on('orderCancelledByUser', onUserCancelledOrder);
    return () => socket.off('orderCancelledByUser', onUserCancelledOrder);
  }, [socket, chatId, currentOrder?.orderId, setTaskCompleted, setMessagesAndSync]);

  useEffect(() => {
    if (!socket || !chatId) return;

    const onChatReset = () => {
      storeSetCurrentOrder(chatId, null);
      useOrderStore.getState()._patch(chatId, {
        taskCompleted: false,
        orderCancelled: false,
        cancellationReason: null,
        completedStatuses: [],
        deliveryMarked: false,
        userConfirmedDelivery: false,
        orderMissing: false,
      });
      setCompletedOrderStatuses([]);
      setAwaitingNewOrder(true);
    };

    socket.on('chatReset', onChatReset);
    return () => socket.off('chatReset', onChatReset);
  }, [socket, chatId]);

  // Register parent push function
  useEffect(() => {
    if (!onRegisterSetMessages) return;
    const pushFromParent = (updater) => {
      isSyncingFromParent.current = true;
      setMessages(prev => typeof updater === 'function' ? updater(prev) : updater);
      queueMicrotask(() => { isSyncingFromParent.current = false; });
    };
    onRegisterSetMessages(pushFromParent, chatId);
  }, [onRegisterSetMessages, chatId]);

  const { permission, requestPermission } = usePushNotifications({
    runnerId, userType: 'runner', socket,
  });

  const { handleTyping, otherUserTyping } = useTypingAndRecordingIndicator({
    socket, chatId, currentUserId: runnerId, currentUserType: 'runner',
  });

  const storedServiceType = useOrderStore(s => s._chats[chatId]?.resolvedServiceType ?? null);

  // ── Derive service type ONCE from currentOrder, locked for this render 
  const resolvedServiceType = useMemo(() => normaliseServiceType(
    selectedUser?.currentRequest?.serviceType
    ?? selectedUser?.serviceType
    ?? storedServiceType
    ?? currentOrder?.serviceType
    ?? currentOrder?.taskType
  ), [selectedUser?._id, currentOrder?.serviceType, currentOrder?.taskType]);

  const isRunErrand = resolvedServiceType === 'run-errand';
  const isPickUp = resolvedServiceType === 'pick-up';
  const isPaid =
    currentOrder?.paymentStatus === 'paid' ||
    currentOrder?.status === 'active' ||
    (!!currentOrder?.orderId && messages.some(
      m => m.type === 'system' &&
        m.text?.toLowerCase().includes('made payment for this task') &&
        m.orderId === currentOrder.orderId
    ));

  // ── Fallback checks against message history — zustand may lag behind 
  const messageTaskCompleted = useMemo(() => messages.some(m =>
    m.type === 'task_completed' || m.messageType === 'task_completed' ||
    m.type === 'task_completed_marker' || m.messageType === 'task_completed_marker' ||
    (m.type === 'system' && m.text?.toLowerCase().includes('task completed'))
  ), [messages]);

  const messageOrderCancelled = useMemo(() => messages.some(m =>
    m.type === 'system' && m.text?.toLowerCase().includes('cancelled this order')
  ), [messages]);

  const effectiveTaskCompleted = taskCompleted || messageTaskCompleted;
  const effectiveOrderCancelled = orderCancelled || messageOrderCancelled;

  const hasActiveOrder = !!currentOrder
    && !['cancelled', 'task_completed'].includes(currentOrder?.status)
    && !effectiveOrderCancelled
    && !effectiveTaskCompleted;

  // ── Stable orderData object passed to OrderStatusFlow 
  const orderFlowData = {
    chatId,
    orderId: currentOrder?.orderId ?? null,
    runnerId,
    userId: selectedUser?._id ?? null,
    serviceType: currentOrder?.serviceType ?? null,
    runnerFleetType: runnerFleetType ?? 'pedestrian',
    deliveryLocation: currentOrder?.deliveryLocation ?? null,
    deliveryCoordinates: currentOrder?.deliveryCoordinates ?? null,
    pickupCoordinates: currentOrder?.pickupCoordinates ?? null,
    pickupLocation: currentOrder?.pickupLocation ?? null,
    marketLocation: currentOrder?.marketLocation ?? null,
    marketCoordinates: currentOrder?.marketCoordinates ?? null,
    usedPayoutSystem: currentOrder?.usedPayoutSystem ?? false,
    userData: selectedUser,
  };

  console.log("orderFlowdata", orderFlowData)

  // Permissions
  useEffect(() => {
    if (runnerId && socket && permission === 'default' && mountedRef.current) {
      requestPermission();
    }
  }, [runnerId, socket, permission, requestPermission]);

  // Sync completedStatusesRef
  useEffect(() => {
    completedStatusesRef.current = completedOrderStatuses;
  }, [completedOrderStatuses]);

  useEffect(() => {
    const tryRestore = () => {
      const storeStatuses = useOrderStore.getState().getChat(chatId).completedStatuses;
      if (storeStatuses?.length > 0) {
        setCompletedOrderStatuses(storeStatuses);
      }
    };

    tryRestore();

    // Also try after hydration settles
    const t = setTimeout(tryRestore, 100);
    return () => clearTimeout(t);
  }, [chatId]);

  // Reset if no order
  useEffect(() => {
    if (selectedUser?._id && runnerId && !currentOrder && mountedRef.current) {
      setDeliveryMarked(false);
      setUserConfirmedDelivery(false);
    }
  }, [selectedUser?._id, runnerId, currentOrder]);

  // Special instructions
  useEffect(() => {
    if (selectedUser?.specialInstructions && mountedRef.current) {
      // Only set if store doesn't already have instructions with fileUrls
      const existing = useOrderStore.getState().getChat(chatId).specialInstructions;
      const hasRealUrls = existing?.media?.some(m => m.fileUrl);
      if (!hasRealUrls) {
        setSpecialInstructions(selectedUser.specialInstructions);
      }
    }
  }, [selectedUser?.specialInstructions]);

  useEffect(() => {
    if (!onSpecialInstructions || !mountedRef.current) return;
    onSpecialInstructions((data) => {
      console.log('[RunnerChat] specialInstructions received:', JSON.stringify(data, null, 2));
      if (mountedRef.current) setSpecialInstructions(data.specialInstructions);
    });
  }, [onSpecialInstructions]);

  // Payout receipt
  useEffect(() => {
    if (!socket || !chatId || !mountedRef.current) return;
    const handler = () => {
      if (mountedRef.current) {
        setCurrentOrder(prev => prev ? { ...prev, usedPayoutSystem: true } : prev);

        socket.emit('getRunnerPayout', { chatId, runnerId, orderId: currentOrder?.orderId });
      }
    };
    socket.on('payoutReceiptSubmitted', handler);
    return () => socket.off('payoutReceiptSubmitted', handler);
  }, [socket, chatId, setCurrentOrder]);

  useEffect(() => {
    if (!socket || !chatId || !runnerId || !mountedRef.current) return;
    if (!currentOrder?.orderId) return;
    if (['completed', 'cancelled', 'task_completed'].includes(currentOrder?.status)) return;

    // Always re-fetch when orderId changes
    lastFetchedPayoutOrderIdRef.current = currentOrder.orderId;
    socket.emit('getRunnerPayout', { chatId, runnerId, orderId: currentOrder.orderId });

    const handler = ({ payout }) => {
      if (!mountedRef.current) return;
      if (!payout) return;

      console.log('[RunnerChat] runnerPayoutData received:', payout);

      setCurrentOrder(prev => {
        if (!prev) return prev;
        // Always sync usedPayoutSystem regardless of current value
        if (payout.usedPayoutSystem === prev.usedPayoutSystem) return prev;
        return { ...prev, usedPayoutSystem: payout.usedPayoutSystem ?? false };
      });
    };

    socket.on('runnerPayoutData', handler);

    // Poll every 10s while order is active and payout not yet confirmed
    const pollInterval = setInterval(() => {
      if (!mountedRef.current) return;
      const order = useOrderStore.getState().getChat(chatId).currentOrder;
      if (!order?.orderId) return;
      if (order?.paymentStatus !== 'paid') return;
      if (order?.usedPayoutSystem) {
        clearInterval(pollInterval);
        return;
      }
      socket.emit('getRunnerPayout', { chatId, runnerId, orderId: currentOrder.orderId });
    }, 10000);

    return () => {
      socket.off('runnerPayoutData', handler);
      clearInterval(pollInterval);
    };
  }, [socket, chatId, runnerId, currentOrder?.orderId, setCurrentOrder]);

  // Reset processedMessageIds
  useEffect(() => {
    processedMessageIds.current = new Set();
  }, [selectedUser?._id, runnerId, sessionKey]);

  // Scroll to bottom
  useEffect(() => {
    if (listRef.current && messages.length > 0 && mountedRef.current) {
      const t = setTimeout(() => {
        if (listRef.current) listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [messages, replyingTo]);

  // Camera preview
  useEffect(() => {
    if (capturedImage && isPreviewOpen && !cameraUsedByItemFormRef.current && mountedRef.current) {
      setPreviewImage(capturedImage);
      setShowCameraPreview(true);
    }
  }, [capturedImage, isPreviewOpen]);

  // Special instructions socket handler
  useEffect(() => {
    if (!onSpecialInstructions || !mountedRef.current) return;
    onSpecialInstructions((data) => {
      if (mountedRef.current) setSpecialInstructions(data.specialInstructions);
    });
  }, [onSpecialInstructions]);

  console.log('[RCS debug]', {
    chatId,
    currentOrder,
    resolvedServiceType,
    isPaid,
    taskCompleted,
    orderCancelled,
    messagesCount: messages.length,
    showOrderFlow,
  });

  // Order created handler — merge new order data, preserving serviceType
  useEffect(() => {
    if (!onOrderCreated || !mountedRef.current) return;
    onOrderCreated((data) => {
      if (!mountedRef.current) return;
      const order = data.order || data;
      if (!order?.orderId) return;

      const isGenuinelyNew = currentOrder?.orderId !== order.orderId;

      setCurrentOrder(prev => (prev?.orderId === order.orderId ? { ...prev, ...order } : order));

      if (isGenuinelyNew) {
        useOrderStore.getState()._patch(chatId, {
          taskCompleted: false,
          orderCancelled: false,
          cancellationReason: null,
          deliveryMarked: false,
          userConfirmedDelivery: false,
        });
        setCompletedOrderStatuses([]);
      }

      setAwaitingNewOrder(false);
      setItemsApproved(false);
    });

  }, [onOrderCreated]);

  // GPS tracking
  useEffect(() => {
    if (!socket || !currentOrder?.orderId || !mountedRef.current) return;
    if (!completedOrderStatuses.includes('en_route_to_delivery')) {
      console.log('[GPS] Not tracking - en_route_to_delivery not in completedStatuses:', completedOrderStatuses);
      return;
    }
    console.log('[GPS] Starting location tracking for order:', currentOrder.orderId);
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!mountedRef.current) return;
        const { latitude, longitude, heading, speed } = position.coords;
        const loc = { lat: latitude, lng: longitude, heading: heading || 0, speed: speed || 0 };
        setRunnerLocation(loc);
        socket.emit('runner:locationUpdate', { orderId: currentOrder.orderId, ...loc });
      },
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
    return () => { navigator.geolocation.clearWatch(watchId); setRunnerLocation(null); };
  }, [socket, currentOrder?.orderId, completedOrderStatuses]);

  // Delivery confirmed/denied
  useEffect(() => {
    if (!socket || !chatId || !mountedRef.current) return;
    const onConfirmed = () => {
      if (mountedRef.current) {
        setUserConfirmedDelivery(true);
        // user confirmed — close user_wont_confirm_delivery window
        clearTimeout(deliveryDisputeTimerRef.current);
        useOrderStore.getState().setDeliveryDisputeWindowOpen(chatId, false);
      }
    };
    const onDenied = () => {
      if (mountedRef.current) {
        setUserConfirmedDelivery(false);
        setDeliveryMarked(false);
        setMarkingDelivery(false);

        useOrderStore.getState().setDeliveryDenied(chatId, true);
        setItemsApproved(false);
      }
    };
    const onTaskCompleted = ({ orderId }) => {
      if (!mountedRef.current) return;
      setTaskCompleted(true);
    };

    const onDisputeRaised = ({ orderId }) => {
      setCurrentOrder(prev =>
        prev?.orderId === orderId ? { ...prev, hasDispute: true } : prev
      );
    };

    socket.on('taskCompleted', onTaskCompleted)
    socket.on('deliveryConfirmed', onConfirmed);
    socket.on('deliveryAutoConfirmed', onConfirmed);
    socket.on('deliveryDenied', onDenied);
    socket.on('disputeRaised', onDisputeRaised);

    return () => {
      socket.off('deliveryConfirmed', onConfirmed);
      socket.off('deliveryAutoConfirmed', onConfirmed);
      socket.off('deliveryDenied', onDenied);
      socket.off('taskCompleted', onTaskCompleted);
      socket.off('disputeRaised', onDisputeRaised);
      clearTimeout(deliveryDisputeTimerRef.current);
    };
  }, [socket, chatId]);

  // ── Item submission error handler ──────────────────────────────────────────
  useEffect(() => {
    if (!socket || !mountedRef.current) return;

    const handleItemSubmissionError = ({ error, submissionId, retryable, originalPayload }) => {
      if (!mountedRef.current) return;
      setAttachFlowResetKey(k => k + 1);

      // No payload to retry with — just show the error immediately
      if (!originalPayload || retryable === false) {
        setMessagesAndSync(prev => [...prev, {
          id: `item-submit-error-${Date.now()}`,
          from: 'system', type: 'system', messageType: 'system',
          text: error || 'Failed to submit items. Please try again.',
          time: getCurrentTime(),
          senderId: 'system', senderType: 'system',
          style: 'error', retryable: false, retryAction: 'submitItems',
        }]);
        return;
      }

      const retryId = `items-retrying-${submissionId || Date.now()}`;

      // Show "Retrying..." immediately on first server error
      setMessagesAndSync(prev => [...prev, {
        id: retryId,
        from: 'system', type: 'system', messageType: 'system',
        text: 'Failed to submit items. Retrying… (1/5)',
        time: getCurrentTime(),
        senderId: 'system', senderType: 'system',
        style: 'warning',
      }]);

      submitItemsWithRetry(
        socket,
        originalPayload,
        // onRetry — updates the retrying message each attempt
        (attempt, max) => {
          if (!mountedRef.current) return;
          setMessagesAndSync(prev => prev.map(m =>
            m.id === retryId
              ? { ...m, text: `Failed to submit items. Retrying… (${attempt}/${max})` }
              : m
          ));
        },
      )
        .then(() => {
          // Success — remove the retrying message silently
          if (mountedRef.current) {
            setMessagesAndSync(prev => prev.filter(m => m.id !== retryId));
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          // All 5 retries exhausted — swap retrying message for final error
          setMessagesAndSync(prev => [
            ...prev.filter(m => m.id !== retryId),
            {
              id: `item-submit-error-${Date.now()}`,
              from: 'system', type: 'system', messageType: 'system',
              text: 'Failed to submit items. Please try again.',
              time: getCurrentTime(),
              senderId: 'system', senderType: 'system',
              style: 'error', retryable: true, retryAction: 'submitItems',
            },
          ]);
        });
    };

    const handlePickupItemSubmissionError = ({ error, submissionId, retryable, originalPayload }) => {
      if (!mountedRef.current) return;
      setAttachFlowResetKey(k => k + 1);

      if (!originalPayload || retryable === false) {
        setMessagesAndSync(prev => [...prev, {
          id: `pickup-submit-error-${Date.now()}`,
          from: 'system', type: 'system', messageType: 'system',
          text: error || 'Failed to submit pickup item. Please try again.',
          time: getCurrentTime(),
          senderId: 'system', senderType: 'system',
          style: 'error', retryable: false, retryAction: 'submitPickupItem',
        }]);
        return;
      }

      const retryId = `pickup-retrying-${submissionId || Date.now()}`;

      setMessagesAndSync(prev => [...prev, {
        id: retryId,
        from: 'system', type: 'system', messageType: 'system',
        text: 'Failed to submit pickup item. Retrying… (1/5)',
        time: getCurrentTime(),
        senderId: 'system', senderType: 'system',
        style: 'warning',
      }]);

      submitPickupItemWithRetry(
        socket,
        originalPayload,
        (attempt, max) => {
          if (!mountedRef.current) return;
          setMessagesAndSync(prev => prev.map(m =>
            m.id === retryId
              ? { ...m, text: `Failed to submit pickup item. Retrying… (${attempt}/${max})` }
              : m
          ));
        },
      )
        .then(() => {
          if (mountedRef.current) {
            setMessagesAndSync(prev => prev.filter(m => m.id !== retryId));
          }
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setMessagesAndSync(prev => [
            ...prev.filter(m => m.id !== retryId),
            {
              id: `pickup-submit-error-${Date.now()}`,
              from: 'system', type: 'system', messageType: 'system',
              text: 'Failed to submit pickup item. Please try again.',
              time: getCurrentTime(),
              senderId: 'system', senderType: 'system',
              style: 'error', retryable: true, retryAction: 'submitPickupItem',
            },
          ]);
        });
    };

    socket.on('itemSubmissionError', handleItemSubmissionError);
    socket.on('pickupItemSubmissionError', handlePickupItemSubmissionError);

    return () => {
      socket.off('itemSubmissionError', handleItemSubmissionError);
      socket.off('pickupItemSubmissionError', handlePickupItemSubmissionError);
    };
  }, [socket, setMessagesAndSync]);

  useEffect(() => {
    if (!onDeliveryConfirmed || !mountedRef.current) return;
    onDeliveryConfirmed(() => {
      if (mountedRef.current) setDeliveryMarked(false);
    });
  }, [onDeliveryConfirmed]);

  // Message deleted
  useEffect(() => {
    if (!onMessageDeleted || !mountedRef.current) return;
    onMessageDeleted(({ messageId, deletedBy }) => {
      if (!mountedRef.current) return;
      const isMe = deletedBy === runnerId;
      setMessagesAndSync(prev => prev.map(msg => msg.id === messageId
        ? { ...msg, deleted: true, text: isMe ? "You deleted this message" : "This message was deleted", type: "deleted", fileUrl: null, fileName: null }
        : msg
      ));
    });
  }, [onMessageDeleted, runnerId, setMessagesAndSync]);

  // Incoming message listener
  useEffect(() => {
    if (!socket || !chatId || !mountedRef.current) return;

    const handleIncomingMessage = (msg) => {
      // drop server echos
      if (!mountedRef.current) return;

      if (msg.type === 'payment_confirmed' || msg.messageType === 'payment_confirmed') return;

      if (
        (msg.type === 'item_submission' || msg.messageType === 'item_submission' ||
          msg.type === 'pickup_item_submission' || msg.messageType === 'pickup_item_submission') &&
        msg.senderId === runnerId
      ) return;

      if (
        msg.type === 'system' &&
        STATUS_LABELS.has(msg.text)
      ) return;

      if (
        msg.type === 'system' &&
        msg.text?.toLowerCase().includes('cancelled this order')
      ) return;

      const isSpecialType = [
        'payment_request', 'payment_success', 'payment_confirmed',
        'delivery_confirmation_request', 'item_submission', 'pickup_item_submission',
        'dispute_raised', 'dispute_resolved', 'tracking',
      ].includes(msg.type || msg.messageType);

      if (!isSpecialType && !msg.text && !msg.fileUrl && !msg.fileName) return;

      if (processedMessageIds.current.has(msg.id)) return;
      processedMessageIds.current.add(msg.id);

      if (msg.seq) {
        const current = lastSeqRef.current.get(chatId) || 0;
        if (msg.seq > current) lastSeqRef.current.set(chatId, msg.seq);
      }

      if (msg.type === 'fileUploadSuccess' || msg.messageType === 'fileUploadSuccess') return;
      if (msg.type === 'system' && msg.text?.includes('must approve the items you sent')) return;

      if (msg.type === 'order_created' || (msg.type === 'system' && msg.text?.includes('order created')) || (msg.type === 'payment_request' && msg.paymentData?.orderId)) {
        const newOrderId = msg.paymentData?.orderId || msg.orderId;
        if (newOrderId && currentOrder?.orderId !== newOrderId) {
          setTaskCompleted(false);
          setDeliveryMarked(false);
          setUserConfirmedDelivery(false);
        }
      }

      if (msg.id?.startsWith('delivery-confirmed-runner-') ||
        msg.type === 'delivery_confirmation' ||
        msg.messageType === 'delivery_confirmation') {
        setUserConfirmedDelivery(true);
      }

      if (
        msg.type === 'task_completed' || msg.messageType === 'task_completed' ||
        msg.type === 'task_completed_marker' || msg.messageType === 'task_completed_marker' ||
        (msg.type === 'system' && msg.text?.toLowerCase().includes('task completed'))
      ) {
        setTaskCompleted(true);
      }

      if (msg.paymentConfirmed && msg.type === 'system') {
        setCurrentOrder(prev => ({ ...(prev || {}), paymentStatus: 'paid', status: 'active' }));
      }

      const formattedMsg = {
        ...msg,
        from: (msg.from === 'system' || msg.type === 'system' || msg.senderType === 'system' || msg.senderId === 'system')
          ? 'system' : msg.senderId === runnerId ? 'me' : 'them',
        type: msg.type || msg.messageType || 'text',
      };

      if (msg.type === 'system' && msg.text?.includes('approved the items')) {
        formattedMsg.itemsApproved = true;
      }

      setMessagesAndSync(prev => {
        if (msg.tempId) {
          const hasTmp = prev.some(m => m.id === msg.tempId || m.tempId === msg.tempId);
          if (hasTmp) return prev.map(m => (m.id === msg.tempId || m.tempId === msg.tempId) ? formattedMsg : m);
        }
        const exists = prev.some(m => m.id === msg.id);
        if (exists) return prev.map(m => m.id === msg.id ? { ...m, ...formattedMsg } : m);
        return [...prev, formattedMsg];
      });
    };

    socket.on('message', handleIncomingMessage);
    return () => socket.off('message', handleIncomingMessage);
  }, [socket, chatId, runnerId, setMessagesAndSync, setCurrentOrder, setTaskCompleted, currentOrder?.orderId]);

  // Item submission update
  useEffect(() => {
    if (!socket || !mountedRef.current) return;
    const handler = (data) => {
      if (!mountedRef.current) return;
      setMessagesAndSync(prev => prev.map(m =>
        m.submissionId === data.submissionId || m.id === data.submissionId
          ? { ...m, status: data.status, rejectionReason: data.rejectionReason }
          : m
      ));
    };
    socket.on('itemSubmissionUpdated', handler);
    return () => socket.off('itemSubmissionUpdated', handler);
  }, [socket, setMessagesAndSync]);

  useEffect(() => {
    if (!socket || !mountedRef.current) return;

    const handleItemApproved = (data) => {
      if (!mountedRef.current) return;
      console.log('[RunnerChat] itemSubmissionApproved received:', data);
      setItemsApproved(true);
    };

    // Also catch it from the existing itemSubmissionUpdated shape
    const handleSubmissionUpdated = (data) => {
      if (!mountedRef.current) return;
      if (data.status === 'approved') setItemsApproved(true);
    };

    socket.on('itemSubmissionApproved', handleItemApproved);
    socket.on('itemSubmissionUpdated', handleSubmissionUpdated);

    return () => {
      socket.off('itemSubmissionApproved', handleItemApproved);
      socket.off('itemSubmissionUpdated', handleSubmissionUpdated);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !mountedRef.current) return;
    const handler = (data) => {
      if (!mountedRef.current) return;
      if (data.status === 'approved') setItemsApproved(true);
      setMessagesAndSync(prev => prev.map(m =>
        m.submissionId === data.submissionId || m.id === data.submissionId
          ? { ...m, status: data.status, rejectionReason: data.rejectionReason }
          : m
      ));
    };
    socket.on('pickupItemUpdated', handler);
    return () => socket.off('pickupItemUpdated', handler);
  }, [socket, setMessagesAndSync]);

  // Reconnect / missed messages
  useEffect(() => {
    if (!socket || !chatId || !mountedRef.current) return;

    const handleReconnect = () => {
      flushSocketQueue(socket);
      const lastSeq = lastSeqRef.current.get(chatId) || 0;
      socket.emit('getMissedMessages', { chatId, fromSeq: lastSeq });
    };

    const handleMissedMessages = (msgs) => {
      if (!mountedRef.current) return;
      if (!msgs?.length) return;
      setMessagesAndSync(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const toAdd = msgs
          .filter(m => !existingIds.has(m.id) && !processedMessageIds.current.has(m.id))
          .map(msg => {
            processedMessageIds.current.add(msg.id);
            if (msg.paymentConfirmed || (msg.type === 'system' && msg.text?.toLowerCase().includes('made payment for this task'))) {
              setCurrentOrder(p => p ? { ...p, paymentStatus: 'paid', status: 'active' } : p);
            }
            const isSystem = msg.from === 'system' || msg.type === 'system' || msg.messageType === 'system' || msg.senderType === 'system' || msg.senderId === 'system';
            return {
              ...msg,
              from: isSystem ? 'system' : (msg.senderId === runnerId ? 'me' : 'them'),
              type: msg.type || msg.messageType || 'text',
            };
          });
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    };

    socket.on('connect', handleReconnect);
    socket.on('missedMessages', handleMissedMessages);

    // flush immediately on mount too
    if (socket.connected) flushSocketQueue(socket);

    return () => {
      if (!socket) return;
      socket.off('connect', handleReconnect);
      socket.off('missedMessages', handleMissedMessages);
    };
  }, [socket, chatId, runnerId, setMessagesAndSync, setCurrentOrder, setCompletedOrderStatuses]);

  // ── Message actions ────────────────────────────────────────────────────────
  const handleDeleteMessage = useCallback((messageId, deleteForEveryone = false) => {
    setMessagesAndSync(prev => prev.map(msg => msg.id === messageId
      ? { ...msg, deleted: true, text: "You deleted this message", type: "deleted", fileUrl: null, fileName: null }
      : msg
    ));

    if (deleteForEveryone && chatId) {
      const payload = { chatId, messageId, userId: runnerId, deleteForEveryone: true };
      if (socket?.connected) {
        socket.emit('deleteMessage', payload);
      } else {
        enqueueSocketEvent('deleteMessage', payload);
      }
    }
  }, [socket, chatId, runnerId, setMessagesAndSync]);

  const handleEditMessage = useCallback((messageId, newText) => {
    setMessagesAndSync(prev => prev.map(msg => msg.id === messageId ? { ...msg, text: newText, edited: true } : msg));
  }, [setMessagesAndSync]);

  const handleMessageReact = useCallback((messageId, emoji) => {
    setMessagesAndSync(prev => prev.map(msg => msg.id === messageId ? { ...msg, reaction: emoji } : msg));

    const payload = { chatId, messageId, emoji, userId: runnerId };
    if (socket?.connected) {
      socket.emit('reactToMessage', payload);
    } else {
      enqueueSocketEvent('reactToMessage', payload);
    }
  }, [socket, chatId, runnerId, setMessagesAndSync]);

  const handleMessageReply = useCallback((message) => {
    setReplyingTo(message);
    setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 100);
  }, [setReplyingTo]);

  const handleCancelReply = useCallback(() => setReplyingTo(null), [setReplyingTo]);

  const handleScrollToMessage = useCallback((messageId) => {
    const el = document.getElementById(`message-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('highlight-message');
      setTimeout(() => el.classList.remove('highlight-message'), 2000);
    }
  }, []);

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { alert(`"${file.name}" exceeds 10MB limit.`); continue; }
      let msgType = 'file';
      if (file.type.startsWith('image/')) msgType = 'image';
      else if (file.type.startsWith('video/')) msgType = 'video';
      else if (file.type.startsWith('audio/')) msgType = 'audio';
      const tempId = `temp-${Date.now()}-${file.name}`;
      const localUrl = URL.createObjectURL(file);
      setMessagesAndSync(prev => [...prev, {
        id: tempId, tempId, from: 'me', type: msgType,
        fileName: file.name, fileType: file.type, fileUrl: localUrl, text: '',
        time: getCurrentTime(),
        status: 'uploading', senderId: runnerId, senderType: 'runner',
        isUploading: true, createdAt: new Date().toISOString(),
      }]);
      try {
        await uploadFileWithProgress(file, { chatId, senderId: runnerId, senderType: 'runner', tempId, type: msgType });
      } catch (error) {
        console.error('Upload error:', error);
        setMessagesAndSync(prev => prev.filter(m => m.id !== tempId));
        URL.revokeObjectURL(localUrl);
      }
    }
    event.target.value = '';
  }, [uploadFileWithProgress, chatId, runnerId, setMessagesAndSync]);

  const handleAttachClickInternal = useCallback(() => fileInputRef.current?.click(), []);

  const handleSendPhoto = useCallback(async (image, replyText) => {
    if (!selectedUser || !runnerId) return;
    try {
      const blob = await (await fetch(image)).blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const tempId = `temp-${Date.now()}-photo`;
      setMessagesAndSync(prev => [...prev, {
        id: tempId, tempId, from: 'me', type: 'image',
        fileName: file.name, fileType: 'image/jpeg', fileUrl: image, text: replyText || '',
        time: getCurrentTime(),
        status: 'uploading', senderId: runnerId, senderType: 'runner', isUploading: true,
        ...(replyingTo && { replyTo: replyingTo.id, replyToMessage: replyingTo.text || replyingTo.fileName || 'Media', replyToFrom: replyingTo.from }),
      }]);
      await uploadFileWithProgress(file, {
        chatId, senderId: runnerId, senderType: 'runner', tempId, text: replyText || '', type: 'image',
        ...(replyingTo && { replyTo: replyingTo.id, replyToMessage: replyingTo.text || replyingTo.fileName || 'Media', replyToFrom: replyingTo.from }),
      });
      setShowCameraPreview(false); setPreviewImage(null); closePreview(); setReplyingTo(null);
    } catch (error) {
      console.error('Error sending photo:', error);
      setShowCameraPreview(false); setPreviewImage(null); closePreview();
    }
  }, [selectedUser, runnerId, uploadFileWithProgress, chatId, replyingTo, setMessagesAndSync, closePreview]);

  // ── Item submission ────────────────────────────────────────────────────────
  const handleSubmitItems = useCallback(async (itemsData) => {
    try {
      const payload = {
        chatId,
        runnerId,
        userId: selectedUser?._id,
        submissionId: `submission-${Date.now()}`,
        escrowId: currentOrder?.escrowId || null,
        items: itemsData.items,
        receiptBase64: itemsData.receiptBase64,
        totalAmount: itemsData.totalAmount,
      };

      if (socket?.connected) {
        socket.emit('submitItems', payload);
      } else {
        enqueueSocketEvent('submitItems', payload);
        console.log('[socketQueue] submitItems queued — socket offline');
      }

      setMessagesAndSync(prev => [...prev, {
        id: `items-submitted-${Date.now()}`,
        from: 'system', type: 'system', messageType: 'system',
        text: `You submitted item(s). ${selectedUser?.firstName || 'User'} must approve the items you sent before marking "Purchase completed".`,
        time: getCurrentTime(),
        status: 'sent', senderId: 'system', senderType: 'system',
        style: 'info', isItemSubmissionProof: true, hasItemPhotos: itemsData.hasItemPhotos ?? false,
      }]);
      setShowItemSubmissionForm(false);
    } catch (error) { console.error('Error submitting items:', error); throw error; }
  }, [socket, chatId, runnerId, selectedUser, currentOrder, setMessagesAndSync]);

  const openItemSubmissionForm = useCallback(() => {
    if (!currentOrder) return alert('No active order. Wait for user to place an order.');
    if (!isRunErrand) return alert('Item submission is only for run-errand tasks.');
    if (currentOrder.paymentStatus !== 'paid') return alert('Wait for user to complete payment.');
    setShowItemSubmissionForm(true);
  }, [currentOrder, isRunErrand]);

  // ── Pickup item submission 
  const handleSubmitPickupItem = useCallback(async (itemData) => {
    console.log('[submitPickupItem] called', {
      chatId, runnerId,
      userId: selectedUser?._id,
      photoSize: itemData.photoBase64?.length,
      socketConnected: socket?.connected
    });

    try {
      const payload = {
        chatId,
        runnerId,
        userId: selectedUser?._id,
        submissionId: `pickup-${Date.now()}`,
        itemName: itemData.itemName,
        photoBase64: itemData.photoBase64,
      };

      if (socket?.connected) {
        socket.emit('submitPickupItem', payload);
      } else {
        enqueueSocketEvent('submitPickupItem', payload);
        console.log('[socketQueue] submitPickupItem queued — socket offline');
      }

      setMessagesAndSync(prev => [...prev, {
        id: `pickup-submitted-${Date.now()}`,
        from: 'system',
        type: 'pickup_item_submission',
        messageType: 'pickup_item_submission',
        text: `You submitted pickup item: "${itemData.itemName}". ${selectedUser?.firstName || 'User'} must approve before you can mark as collected.`,
        time: getCurrentTime(),
        senderId: 'system', senderType: 'system',
        style: 'info', isPickupSubmission: true,
        pickupItemName: itemData.itemName,
        pickupPhotoUrl: itemData.photoUrl,
        status: 'pending',
      }]);
      setShowPickupItemForm(false);
    } catch (error) {
      console.error('Error submitting pickup item:', error);
      throw error;
    }
  }, [socket, chatId, runnerId, selectedUser, setMessagesAndSync]);

  const openPickupItemForm = useCallback(() => {
    console.log('[openPickupItemForm]', {
      currentOrder: !!currentOrder,
      isPickUp,
      paymentStatus: currentOrder?.paymentStatus,
      status: currentOrder?.status,
      resolvedServiceType,
    });

    if (!currentOrder) return alert('No active order. Wait for user to place an order.');
    console.log("selectedUser.currentRequest:", selectedUser?.currentRequest);
    console.log("selectedUser.serviceType:", selectedUser?.serviceType);
    console.log("resolvedServiceType:", resolvedServiceType, "currentOrder.serviceType:", currentOrder?.serviceType, "currentOrder.taskType:", currentOrder?.taskType);
    if (!isPickUp) return alert('Item submission is only for pick-up tasks.');
    if (currentOrder.paymentStatus !== 'paid') return alert('Wait for user to complete payment.');
    setShowPickupItemForm(true);
  }, [currentOrder, isPickUp]);

  // ── Delivery ───────────────────────────────────────────────────────────────
  const handleMarkDeliveryComplete = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!currentOrder || !chatId) return reject(new Error('Missing data'));
      if (!isPaid) return reject(new Error('Cannot mark delivery complete before payment'));
      if (!currentOrder?.orderId) return reject(new Error('No active order'));

      if (isRunErrand) {
        const storeMessages = useOrderStore.getState().getChat(chatId).messages ?? [];
        const approved = storeMessages.some(m =>
          (m.type === 'item_submission' || m.messageType === 'item_submission') && m.status === 'approved'
        ) || storeMessages.some(m =>
          m.type === 'system' && m.text?.toLowerCase().includes('approved the items')
        );
        if (!approved) return reject(new Error('Items must be approved before marking delivery complete.'));
      }

      const payload = { chatId, orderId: currentOrder.orderId, runnerId, deliveryProof: null };
      const optimisticMsgId = `delivery-marked-runner-${Date.now()}`;

      setMarkingDelivery(true);
      setDeliveryMarked(true);
      setMessagesAndSync(prev => [...prev, {
        id: optimisticMsgId,
        from: 'system', type: 'system', messageType: 'system',
        text: 'You marked delivery as complete. Waiting for the user to confirm.',
        time: getCurrentTime(),
        status: 'sent', senderId: 'system', senderType: 'system',
      }]);

      if (!socket?.connected) {
        enqueueSocketEvent('markDeliveryComplete', payload);
        setMarkingDelivery(false);
        return resolve();
      }

      const revert = () => {
        setDeliveryMarked(false);
        setMarkingDelivery(false);
        setMessagesAndSync(prev => prev.filter(m => m.id !== optimisticMsgId));
      };

      const onError = (err) => {
        socket.off('error', onError);
        socket.off('deliveryMarkedComplete', onSuccess);
        revert();
        reject(new Error(err.message));
      };

      const onSuccess = () => {
        socket.off('error', onError);
        socket.off('deliveryMarkedComplete', onSuccess);
        deliveryDisputeTimerRef.current = setTimeout(() => {
          useOrderStore.getState().setDeliveryDisputeWindowOpen(chatId, true);
        }, 10 * 60 * 1000);
        setMarkingDelivery(false);
        useOrderStore.getState().setDeliveryDenied(chatId, false);
        setCurrentOrder(prev => prev ? { ...prev, status: 'item_delivered' } : prev);
        useOrderStore.getState().mergeCurrentOrder(chatId, { status: 'item_delivered' });
        resolve();
      };

      socket.once('error', onError);
      socket.once('deliveryMarkedComplete', onSuccess);
      socket.emit('markDeliveryComplete', payload);

      setTimeout(() => {
        socket.off('error', onError);
        socket.off('deliveryMarkedComplete', onSuccess);
        revert();
        reject(new Error('No response from server'));
      }, 10000);
    });
  }, [socket, currentOrder, chatId, runnerId, isPaid, isRunErrand, setDeliveryMarked, setMessagesAndSync]);

  const handleStatusMessage = useCallback((systemMessage) => {
    setMessagesAndSync(prev => {
      if (prev.some(m => m.id === systemMessage.id)) return prev;
      if (STATUS_LABELS.has(systemMessage.text) && prev.some(m => m.text === systemMessage.text)) return prev;
      return [...prev, systemMessage];
    });
  }, [setMessagesAndSync]);

  const handleTextChange = useCallback((e) => {
    setText(e.target.value);
    handleTyping();
  }, [setText, handleTyping]);

  const handleKeyDown = useCallback(() => handleTyping(), [handleTyping]);

  const getFirstLetter = useCallback((name) => name ? name.charAt(0).toUpperCase() : 'U', []);
  const getRandomBgColor = useCallback((name) => {
    if (!name) return 'bg-green-500';
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-green-500', 'bg-teal-500', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500'];
    return colors[name.charCodeAt(0) % colors.length];
  }, []);

  const callerName = selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName || ''}` : 'User';
  const callerAvatar = selectedUser?.avatar || null;

  const TypingIndicator = useCallback(() => (
    <div className="flex items-center gap-2 px-4 py-2">
      <div className="flex gap-1">
        {[0, 150, 300].map((d, i) => (
          <span key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
      <span className="text-sm text-gray-500">typing...</span>
    </div>
  ), []);

  if (awaitingNewOrder) return (
    <div className="fixed inset-0 bg-black-100 z-50 flex flex-col items-center justify-center gap-4">
      <BarLoader fullScreen />
      <p className={`text-sm font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
        Preparing your order...
      </p>
    </div>
  );

  return (
    <>
      {callState !== "idle" && callType === "voice" && (
        <CallScreen
          callState={callState} callType={callType} callerName={callerName} callerAvatar={callerAvatar}
          isMuted={isMuted} isCameraOff={isCameraOff} formattedDuration={formattedDuration}
          remoteUsers={remoteUsers} localVideoTrack={localVideoTrack}
          onAccept={acceptCall} onDecline={declineCall} onEnd={endCall}
          onToggleMute={toggleMute} onToggleCamera={toggleCamera}
          isConnecting={isConnecting}
          callError={callError}
        />
      )}

      {callState !== "idle" && callType === "video" && (
        <VideoCallScreen
          callState={callState} callType={callType} callerName={callerName} callerAvatar={callerAvatar}
          isMuted={isMuted} isCameraOff={isCameraOff} isSpeakerOn={isSpeakerOn}
          formattedDuration={formattedDuration} remoteUsers={remoteUsers} localVideoTrack={localVideoTrack}
          networkQuality={networkQuality} darkMode={dark}
          onAccept={acceptCall} onDecline={declineCall} onEnd={endCall}
          onToggleMute={toggleMute} onToggleCamera={toggleCamera}
          onSwitchCamera={switchCamera} onToggleSpeaker={toggleSpeaker}
          isConnecting={isConnecting}
          callError={callError}
        />
      )}

      <section className="flex flex-col min-w-0 overflow-hidden h-full scroll-smooth relative">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 min-w-0 px-5 py-3">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
              {selectedUser?.avatar ? (
                <img src={selectedUser.avatar} alt={`${selectedUser.firstName} ${selectedUser.lastName || ''}`} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${getRandomBgColor(selectedUser?.firstName || 'U')} flex items-center justify-center text-white font-bold text-lg`}>
                  {getFirstLetter(selectedUser?.firstName || 'U')}
                </div>
              )}
            </div>
            <div className="truncate">
              <div className="font-bold text-[16px] truncate dark:text-white text-black-200">
                {selectedUser ? `${selectedUser?.firstName} ${selectedUser?.lastName || ''}` : 'User'}
              </div>
              <div className={`text-sm font-medium ${partnerOnline ? 'text-green-500' : 'text-red-400'}`}>
                {partnerOnline ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
          <div className="items-center gap-3 flex">
            <span className="bg-gray-1000 dark:bg-black-200 rounded-full w-10 h-10 flex items-center justify-center">
              <IconButton
                variant="text"
                className={`rounded-full ${!hasActiveOrder ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={!hasActiveOrder}
                onClick={() => { if (hasActiveOrder) initiateCall('video', selectedUser?._id, 'user'); }}
              >
                <Video className="h-6 w-6" />
              </IconButton>
            </span>
            <span className="bg-gray-1000 dark:bg-black-200 rounded-full w-10 h-10 flex items-center justify-center">
              <IconButton
                variant="text"
                className={`rounded-full ${!hasActiveOrder ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={!hasActiveOrder}
                onClick={() => { if (hasActiveOrder) initiateCall('voice', selectedUser?._id, 'user'); }}
              >
                <Phone className="h-6 w-6" />
              </IconButton>
            </span>
            <div className="hidden lg:block pl-2">
              <div
                onClick={() => setDark(!dark)}
                className="cursor-pointer bg-gray-900 dark:bg-gray-100/60 rounded-full w-8 h-8 sm:w-10 sm:h-10 lg:w-10 lg:h-10 flex items-center justify-center hover:scale-105 transition-transform"
              >
                {dark ? <Sun className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" strokeWidth={3.0} />}
              </div>
            </div>
          </div>
        </div>

        {specialInstructions && (
          <SpecialInstructionsBanner
            userName={`${selectedUser?.firstName || 'User'} ${selectedUser?.lastName || ''}`}
            hasText={!!specialInstructions.text}
            mediaCount={specialInstructions.media?.length || 0}
            media={specialInstructions?.media || []}
            onClick={() => setShowSpecialInstructionsModal(true)}
            darkMode={dark}
          />
        )}

        {/* Messages */}
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 py-4 bg-chat-pattern bg-gray-100 dark:bg-black-200">
          <div className="mx-auto max-w-3xl">
            {messages.map((m) => {
              if (m.type === 'dispute_raised' || m.messageType === 'dispute_raised') {
                return (
                  <div key={m.id} className="my-4 flex justify-center">
                    <div className="max-w-sm w-full rounded-2xl p-4 border border-red-500/20 bg-red-500/10">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">

                          <span className="text-red-500 text-sm"><AlertTriangle /></span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold dark:text-white text-black-200">
                            Dispute raised
                          </p>
                          <p className="text-xs mt-1 text-gray-400">
                            {m.text}
                          </p>
                          {m.disputeDetails?.reason && (
                            <p className="text-xs mt-1 font-medium text-red-400">
                              Reason: {m.disputeDetails.reason.replace(/_/g, ' ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-red-500/10 text-xs text-gray-500">
                        Escrow is locked until resolved
                      </div>
                    </div>
                  </div>
                );
              }

              return <Message key={m.id} m={m} darkMode={dark} userType="runner"
                onMessageClick={() => { }} showCursor={false} isChatActive={isChatActive}
                onDelete={handleDeleteMessage} onEdit={handleEditMessage}
                onReact={handleMessageReact} onReply={handleMessageReply}
                onCancelReply={handleCancelReply} messages={messages}
                onScrollToMessage={handleScrollToMessage} showRelativeTime={true}
                disableContextMenu={!hasActiveOrder}
              />
            })}
            {otherUserTyping && <TypingIndicator />}
          </div>
        </div>

        {/* Composer */}
        <div className="onboarding-composer-wrapper bg-gray-100 dark:bg-black-200">
          {effectiveTaskCompleted ? (
            <div className="px-4 py-4">
              <button
                onClick={() => {
                  if (effectiveTaskCompleted || effectiveOrderCancelled) {
                    onBackToHome?.();
                  }
                }}
                disabled={backHomeDisabled}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${backHomeDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-primary hover:opacity-90'}`}
              >
                {backHomeDisabled ? 'Returning...' : 'Back to Home'}
              </button>
            </div>
          ) : effectiveOrderCancelled ? (
            console.log('SHOWING CANCELLED VIEW - orderCancelled is TRUE') ||
            <div>
              <div className={`px-4 py-2 text-center text-sm font-medium ${dark ? 'text-gray-400 bg-black-100' : 'text-gray-500 bg-gray-100'} rounded-xl mx-4 mt-3`}>
                {cancellationReason === 'runner' ? 'You cancelled this order' : 'Order was cancelled by customer'}
              </div>
              <div className="px-4 py-4">
                <button
                  onClick={() => onBackToHome?.()}
                  disabled={backHomeDisabled}
                  className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${backHomeDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-primary hover:opacity-90'}`}
                >
                  {backHomeDisabled ? 'Returning...' : 'Back to Home'}
                </button>
              </div>
            </div>
          ) : orderMissing ? (
            <div>
              <div className={`px-4 py-2 text-center text-sm font-medium ${dark ? 'text-gray-400 bg-black-100' : 'text-gray-500 bg-gray-100'} rounded-xl mx-4 mt-3`}>
                This order could not be found. It may have failed to create.
              </div>
              <div className="px-4 py-4">
                <button
                  onClick={() => onBackToHome?.()}
                  disabled={backHomeDisabled}
                  className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${backHomeDisabled ? 'bg-gray-400 cursor-not-allowed opacity-60' : 'bg-primary hover:opacity-90'}`}
                >
                  {backHomeDisabled ? 'Returning...' : 'Back to Home'}
                </button>
              </div>
            </div>
          ) : (
            console.log('SHOWING NORMAL CHAT - both false', { taskCompleted, orderCancelled }) ||
            <ChatComposer
              isChatActive={isChatActive}
              text={text}
              registrationComplete={true}
              handleKeyDown={handleKeyDown}
              setText={setText}
              selectedUser={selectedUser}
              handleTextChange={handleTextChange}
              send={() => send(replyingTo)}
              handleLocationClick={handleLocationClick}
              handleAttachClick={handleAttachClickInternal}
              fileInputRef={fileInputRef}
              replyingTo={replyingTo}
              onCancelReply={handleCancelReply}
              darkMode={dark}
              setIsAttachFlowOpen={setIsAttachFlowOpen}
              currentOrder={currentOrder}
              uploadFileWithProgress={uploadFileWithProgress}
              chatId={chatId}
              setMessages={setMessagesAndSync}
              runnerId={runnerId}
            />
          )}

          <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx" multiple />

          {showOrderFlow && selectedUser && resolvedServiceType && (
            <OrderStatusFlow
              isOpen={showOrderFlow}
              onClose={() => setShowOrderFlow(false)}
              // ── Single clean orderData object — all identity + location fields ──
              orderData={orderFlowData}
              runnerFleetType={runnerFleetType ?? 'pedestrian'}
              taskType={resolvedServiceType}
              darkMode={dark}
              onStatusClick={handleOrderStatusClick}
              completedStatuses={completedOrderStatuses}
              setCompletedStatuses={setCompletedOrderStatuses}
              socket={socket}
              onStatusMessage={handleStatusMessage}
              messagesRef={{ current: messages }}
              deliveryMarked={deliveryMarked}
              userConfirmedDelivery={userConfirmedDelivery}
            />
          )}

          {isAttachFlowOpen && (
            <AttachmentOptionsFlow
              isOpen={isAttachFlowOpen}
              onClose={() => setIsAttachFlowOpen(false)}
              chatId={chatId}
              markingDelivery={markingDelivery}
              canMarkDelivery={
                completedOrderStatuses.includes('arrived_at_delivery_location') &&
                (isRunErrand ? itemsApproved : isPickUp ? itemsApproved : true)
              }
              onMarkDelivery={() => {
                // if (isRunErrand && !itemsApproved) return;
                setIsAttachFlowOpen(false);
                handleMarkDeliveryComplete();
              }}
              onOpenContactDetails={() => {
                setIsAttachFlowOpen(false);
                setShowContactModal(true);
              }}
              darkMode={dark}
              onSelectCamera={() => { setIsAttachFlowOpen(false); openCamera(); }}
              showSubmitItems={isRunErrand}
              onSubmitItems={() => { setIsAttachFlowOpen(false); openItemSubmissionForm(); }}
              showSubmitPickupItem={isPickUp}
              onSubmitPickupItem={() => { setIsAttachFlowOpen(false); openPickupItemForm(); }}
              serviceType={resolvedServiceType}
              forceReset={attachFlowResetKey}
              messages={messages}
              socket={socket}
              onSelectGallery={() => {
                setIsAttachFlowOpen(false);
                const input = document.createElement('input');
                input.type = 'file'; input.accept = 'image/*,video/*'; input.multiple = false;
                input.onchange = async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const msgType = file.type.startsWith('video/') ? 'video' : 'image';
                  const tempId = `temp-${Date.now()}-gallery`;
                  const localUrl = URL.createObjectURL(file);
                  setMessagesAndSync(prev => [...prev, {
                    id: tempId, tempId, from: 'me', type: msgType,
                    fileName: file.name, fileType: file.type, fileUrl: localUrl, text: '',
                    time: getCurrentTime(),
                    status: 'uploading', senderId: runnerId, senderType: 'runner',
                    isUploading: true, createdAt: new Date().toISOString(),
                  }]);
                  try {
                    await uploadFileWithProgress(file, { chatId, senderId: runnerId, senderType: 'runner', tempId, type: msgType });
                  } catch (err) {
                    console.error('Gallery upload error:', err);
                    setMessagesAndSync(prev => prev.filter(m => m.id !== tempId));
                    URL.revokeObjectURL(localUrl);
                  }
                };
                input.click();
              }}
            />
          )}

          <RecepientContactInformation
            isOpen={showContactModal}
            onClose={() => {
              setShowContactModal(false);
              setIsAttachFlowOpen(true);
            }}
            darkMode={dark}
            currentRequest={currentRequest}
            counterpart={counterpart}
          />

          {showCameraPreview && previewImage && !showItemSubmissionForm && (
            <CameraPreviewModal
              isOpen={showCameraPreview}
              onClose={() => { setShowCameraPreview(false); setPreviewImage(null); closePreview(); }}
              previewImage={previewImage}
              onRetake={() => { setShowCameraPreview(false); setPreviewImage(null); closePreview(); retakePhoto(); }}
              onSend={(image, replyText) => { handleSendPhoto(image, replyText); setShowCameraPreview(false); setPreviewImage(null); closePreview(); }}
              onCancel={() => { setShowCameraPreview(false); setPreviewImage(null); closePreview(); }}
              darkMode={dark}
            />
          )}
        </div>

        {/* Camera */}
        {cameraOpen && (
          <div className="fixed inset-0 bg-black z-[9999] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-4 bg-black/80 flex-shrink-0">
              <Button onClick={closeCamera} className="text-white px-4 py-2 hover:bg-white/10 rounded-lg">Cancel</Button>
              <h3 className="text-white text-lg font-medium">Take Photo</h3>
              <div className="w-16" />
            </div>
            <div className="flex-1 relative bg-black min-h-0">
              {!capturedImage ? (
                <video ref={videoRef} autoPlay playsInline muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }} />
              ) : (
                <img src={capturedImage} alt="Captured"
                  className="absolute inset-0 w-full h-full object-contain bg-black" />
              )}
            </div>
            <div className="flex-shrink-0 bg-black p-4">
              {!capturedImage ? (
                <div className="gap-3 flex justify-center">
                  <Button onClick={capturePhoto} className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 hover:bg-gray-100 shadow-2xl active:scale-95 transition-transform" />
                  <Button onClick={switchCamera} className="text-white px-3 py-2 rounded-lg"><RefreshCw /></Button>
                </div>
              ) : (
                <div className="flex justify-center gap-4">
                  <Button onClick={retakePhoto} className="px-6 py-3 bg-gray-600 text-white rounded-lg shadow-lg hover:bg-gray-700 active:scale-95 transition-transform">Retake</Button>
                  <Button
                    onClick={() => { const photo = capturedImage; closeCamera(); setTimeout(() => { setPreviewImage(photo); setShowCameraPreview(true); }, 100); }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 active:scale-95 transition-transform"
                  >
                    Use Photo
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        <SpecialInstructionsModal
          isOpen={showSpecialInstructionsModal}
          onClose={() => setShowSpecialInstructionsModal(false)}
          userName={`${selectedUser?.firstName || 'User'} ${selectedUser?.lastName || ''}`}
          instructions={specialInstructions}
          darkMode={dark}
        />
      </section>

      {showItemSubmissionForm && (
        <ItemSubmissionForm
          isOpen={showItemSubmissionForm}
          onClose={() => setShowItemSubmissionForm(false)}
          onSubmit={handleSubmitItems}
          darkMode={dark}
          orderBudget={currentOrder?.budget || currentOrder?.itemBudget || 0}
          openCamera={openCamera}
          closeCamera={closeCamera}
          capturePhoto={capturePhoto}
          retakePhoto={retakePhoto}
          capturedImage={capturedImage}
          videoRef={videoRef}
          cameraOpen={cameraOpen}
          isPreviewOpen={isPreviewOpen}
          closePreview={closePreview}
          cameraUsedByItemFormRef={cameraUsedByItemFormRef}
        />
      )}

      {showPickupItemForm && (
        <PickupItemForm
          isOpen={showPickupItemForm}
          onClose={() => setShowPickupItemForm(false)}
          onSubmit={handleSubmitPickupItem}
          darkMode={dark}
          openCamera={openCamera}
          closeCamera={closeCamera}
          capturePhoto={capturePhoto}
          retakePhoto={retakePhoto}
          capturedImage={capturedImage}
          videoRef={videoRef}
          cameraOpen={cameraOpen}
          isPreviewOpen={isPreviewOpen}
          closePreview={closePreview}
          cameraUsedByItemFormRef={cameraUsedByItemFormRef}
        />
      )}

      <OrderPendingModal
        isOpen={orderMissing && !wasOrderMissingOnMountRef.current}
        darkMode={dark}
        userType="runner"
        onLeave={() => onBackToHome?.()}
      />
    </>
  );
}

export default React.memo(RunnerChatScreen);